// Web Audio API based sound effects (no external assets needed)
import { logAttempt } from "@/lib/research";

let audioCtx = null;
let musicNodes = null;

// Real animal sound files (CC-licensed) hosted at upload.wikimedia.org.
// Direct URLs avoid the Special:FilePath redirect chain (which lacks CORS headers).
// upload.wikimedia.org sends Access-Control-Allow-Origin: *.
// Each file verified by reading its Wikimedia ImageDescription metadata — see /app/docs.
const ANIMAL_AUDIO_URLS = {
  // "My cat Silas meowing" — 0.78s
  meow: "https://upload.wikimedia.org/wikipedia/commons/6/62/Meow.ogg",
  // "A dog barking" — 2.6s (previous Dog.ogg was tail-thumping/yawning, not bark)
  woof: "https://upload.wikimedia.org/wikipedia/commons/a/a2/Barking_of_a_dog.ogg",
  // "A cow mooing at the farm" — 3.77s
  moo: "https://upload.wikimedia.org/wikipedia/commons/a/a5/Single_Cow_Moo.ogg",
  // "Ducks snatching... Belgium park" — 3.7s (replaced 16.8s wild Mallard XC112828)
  quack: "https://upload.wikimedia.org/wikipedia/commons/f/f1/Ducks_snatching.ogg",
  // "Sheep 'baa' processed for Tux Paint" — 1.14s (replaced 0.48s less-clear bleat)
  baa: "https://upload.wikimedia.org/wikipedia/commons/2/2a/Mudchute_sheep_1.ogg",
  // "Pig 'oink' processed for Tux Paint" — 0.29s
  oink: "https://upload.wikimedia.org/wikipedia/commons/7/73/Mudchute_pig_1.ogg",
};

// Safety: stop playback after N seconds, in case a file is unexpectedly long
const MAX_ANIMAL_PLAY_MS = 4000;

// Preload audio elements on demand
const audioCache = {};
const getAudio = (key) => {
  if (!ANIMAL_AUDIO_URLS[key]) return null;
  if (!audioCache[key]) {
    const a = new Audio(ANIMAL_AUDIO_URLS[key]);
    a.preload = "auto";
    // Note: do NOT set crossOrigin — only needed for Web Audio API reads.
    // For plain playback, omitting it lets the browser load the resource normally.
    audioCache[key] = a;
  }
  return audioCache[key];
};

const playAnimalAudio = (key, fallbackSynth) => {
  const a = getAudio(key);
  if (!a) {
    fallbackSynth && fallbackSynth();
    return;
  }
  try {
    a.currentTime = 0;
    const p = a.play();
    // Auto-stop after MAX_ANIMAL_PLAY_MS in case the file is longer than expected
    setTimeout(() => {
      if (!a.paused) {
        try {
          a.pause();
          a.currentTime = 0;
        } catch (_) {
          // ignore
        }
      }
    }, MAX_ANIMAL_PLAY_MS);
    if (p && typeof p.catch === "function") {
      p.catch((err) => {
        console.warn(`Animal audio "${key}" play failed, using synthesis:`, err.message);
        fallbackSynth && fallbackSynth();
      });
    }
  } catch (err) {
    console.warn(`Animal audio "${key}" exception:`, err);
    fallbackSynth && fallbackSynth();
  }
};

const getCtx = () => {
  if (!audioCtx) {
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      console.warn("Audio not supported");
    }
  }
  return audioCtx;
};

const playTone = (freq, duration = 0.18, type = "sine", gain = 0.18) => {
  const ctx = getCtx();
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.value = 0;
  g.gain.linearRampToValueAtTime(gain, ctx.currentTime + 0.02);
  g.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);
  osc.connect(g);
  g.connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + duration + 0.02);
};

// Synthesis fallbacks (used if real audio fails to load)
const synth = {
  meow: () => {
    playTone(700, 0.18, "sawtooth", 0.12);
    setTimeout(() => playTone(560, 0.22, "sawtooth", 0.12), 150);
  },
  woof: () => {
    playTone(180, 0.12, "square", 0.18);
    setTimeout(() => playTone(140, 0.16, "square", 0.18), 130);
  },
  moo: () => playTone(110, 0.4, "sawtooth", 0.16),
  quack: () => {
    playTone(380, 0.1, "square", 0.16);
    setTimeout(() => playTone(320, 0.12, "square", 0.16), 90);
  },
  baa: () => {
    playTone(330, 0.18, "sawtooth", 0.14);
    setTimeout(() => playTone(310, 0.18, "sawtooth", 0.14), 130);
  },
  oink: () => {
    playTone(220, 0.12, "square", 0.18);
    setTimeout(() => playTone(180, 0.14, "square", 0.18), 110);
  },
};

export const sfx = {
  pop: () => {
    playTone(700, 0.08, "triangle", 0.22);
    setTimeout(() => playTone(900, 0.06, "triangle", 0.18), 30);
  },
  tap: () => playTone(520, 0.1, "sine", 0.16),
  success: () => {
    playTone(523, 0.14, "triangle", 0.22);
    setTimeout(() => playTone(659, 0.14, "triangle", 0.22), 130);
    setTimeout(() => playTone(784, 0.22, "triangle", 0.22), 260);
  },
  wrong: () => {
    playTone(220, 0.18, "sawtooth", 0.12);
  },
  giggle: () => {
    const ctx = getCtx();
    if (!ctx) return;
    [880, 988, 1175, 988, 880].forEach((f, i) => {
      setTimeout(() => playTone(f, 0.07, "triangle", 0.14), i * 70);
    });
  },
  star: () => {
    [880, 1175, 1568].forEach((f, i) => {
      setTimeout(() => playTone(f, 0.12, "sine", 0.18), i * 80);
    });
  },
  meow: () => playAnimalAudio("meow", synth.meow),
  woof: () => playAnimalAudio("woof", synth.woof),
  moo: () => playAnimalAudio("moo", synth.moo),
  quack: () => playAnimalAudio("quack", synth.quack),
  baa: () => playAnimalAudio("baa", synth.baa),
  oink: () => playAnimalAudio("oink", synth.oink),
};

// Trigger browser to start loading animal sounds in background on first user gesture
let preloaded = false;
export const preloadAnimalSounds = () => {
  if (preloaded) return;
  preloaded = true;
  Object.keys(ANIMAL_AUDIO_URLS).forEach((k) => getAudio(k));
};

export const speak = (text, lang = "id", soundOn = true) => {
  if (!soundOn || !("speechSynthesis" in window)) return;
  try {
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = lang === "id" ? "id-ID" : "en-US";
    utter.rate = 0.95;
    utter.pitch = 1.25;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
  } catch (err) {
    console.warn("Speech synthesis failed:", err);
  }
};

export const playSfx = (name, soundOn = true) => {
  // Auto-track research attempts when success/wrong feedback plays
  if (name === "success") logAttempt(true);
  else if (name === "wrong") logAttempt(false);
  if (!soundOn) return;
  const fn = sfx[name];
  if (fn) fn();
};

// Simple looping cute melody
let bgAudio = null;

// Self-hosted BGM served from our own domain for reliability.
// Wikimedia blocks non-browser requests; hosting locally avoids CORS/403 issues.
const BG_MUSIC_URL = "/bg_music.mp3";

export const startMusic = () => {
  const ctx = getCtx();
  if (!ctx) return;

  // Ensure AudioContext is active
  if (ctx.state === "suspended") {
    ctx.resume().catch(e => console.warn("Failed to resume AudioContext:", e));
  }

  if (!bgAudio) {
    console.log("Initializing background music...");
    bgAudio = new Audio(BG_MUSIC_URL);
    bgAudio.loop = true;
    bgAudio.volume = 0.3; // Increased to 30% for better audibility
    bgAudio.crossOrigin = "anonymous"; // Good practice for external assets
  }

  const playPromise = bgAudio.play();
  
  if (playPromise !== undefined) {
    playPromise
      .then(() => {
        console.log("Background music started playing successfully.");
      })
      .catch((err) => {
        console.warn("Background music autoplay was prevented. Waiting for next interaction.", err.message);
        
        // Strategy: retry playing on the next document click if blocked
        const retryPlay = () => {
          bgAudio.play()
            .then(() => {
              console.log("Background music started after user interaction.");
              document.removeEventListener("click", retryPlay);
            })
            .catch(e => console.warn("Retry play failed:", e));
        };
        document.addEventListener("click", retryPlay, { once: true });
      });
  }
};

export const stopMusic = () => {
  if (bgAudio) {
    try {
      console.log("Stopping background music...");
      bgAudio.pause();
    } catch (e) {
      console.warn("Error stopping background music:", e);
    }
  }
};
